import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Title, Text, Card, Badge, Button, Group, Stack, Drawer, ActionIcon, Tooltip, SimpleGrid } from '@mantine/core';
import { IconSearch, IconEye, IconRefresh, IconAdjustments, IconListDetails } from '@tabler/icons-react';
import { Flex, Select, TextInput, CCMSDataTable, StreamProgress, StatusKPICard, StatusKPISkeleton, getStatusIcon, ListPagination } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { sortRows } from '@/utils/tableSort';
import { useACCourseService, useRegistrationStatusService, useFacilitatorService, RegistrationListView as RegistrationRow, RegistrationStatus, RegistrationFilterModel, useRegistrationStreaming } from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import ExportCsvButton from '@/components/common/ExportCsvButton';
import { matchesCampus } from '@/components/common/CampusRole';

const PAGE_SIZE = 10;

/**
 * Statuses that get a KPI card, in display order (an "All" card is prepended).
 *
 * Matched on name because registration status IDs are assigned per site rather than fixed by the
 * backend contract, so renaming one of these in /LookUp will drop its card.
 */
const KPI_STATUS_NAMES = ['pending', 'active', 'completed'];

/** Cards actually rendered: the statuses above plus "All". Drives the grid width and the skeletons. */
const KPI_CARD_COUNT = KPI_STATUS_NAMES.length + 1;

interface RegistrationsListViewProps {
  /** Locks the page to a single statusID (e.g. 0 = Pending, 1 = Active, -1 = Cancelled).
   *  When set, the KPI cards are hidden and the drawer's Status field is pinned. */
  fixedStatusID?: number;
  title?: string;
  subtitle?: string;
  /** Campus slug from the URL (e.g. "North"); only used to build the details link. */
  campus: string;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function CampusRegistrationsListView({ fixedStatusID, title, subtitle, campus }: RegistrationsListViewProps) {
  const navigate            = useNavigate();
  const openRegistration = (registrationID: number) =>
    navigate(`/Grow/CampusRegistrations/${encodeURIComponent(campus)}/${registrationID}`);
  const courseService       = useACCourseService();
  const statusService       = useRegistrationStatusService();
  const facilitatorService  = useFacilitatorService();
  const { refetch } = useSidebarCountsContext();

  const showStatusKPIs = fixedStatusID === undefined;

  // ── Table / pagination ──────────────────────────────────────────────────
  const [activePage, setActivePage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [pageSize, setPageSize]     = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');

  // ── Filter draft state ──────────────────────────────────────────────────
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState(
    fixedStatusID !== undefined ? String(fixedStatusID) : '',
  );
  const [filterCourseID, setFilterCourseID] = useState('');
  const [filterDateID, setFilterDateID] = useState('');
  const [filterFacilitatorUserId, setFilterFacilitatorUserId] = useState('');
  const [appliedFilter, setAppliedFilter] = useState<Partial<RegistrationFilterModel>>(
    fixedStatusID !== undefined ? { statusID: String(fixedStatusID) } : {},
  );

  // The pinned status on a status-locked page is not a user-chosen filter, so it does not count
  // towards "filters are active".
  const isFilterActive = Boolean(
    (showStatusKPIs && appliedFilter.statusID) || appliedFilter.courseID ||
    appliedFilter.dateID || appliedFilter.facilitatorUserId,
  );

  // ── Lookup data ─────────────────────────────────────────────────────────
  const [courses, setCourses] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<RegistrationStatus[]>([]);
  const [facilitators, setFacilitators] = useState<any[]>([]);
  const [statusesLoading, setStatusesLoading] = useState(true);
  // Intake dates belong to a course, so this list only fills once a course is picked.
  const [courseDates, setCourseDates] = useState<any[]>([]);

  // ── Confirm delete ──────────────────────────────────────────────────────

  // Keep filterStatus pinned if this instance is locked to a status
  useEffect(() => {
    if (fixedStatusID !== undefined) setFilterStatus(String(fixedStatusID));
  }, [fixedStatusID]);

  const visibleStatuses = useMemo(
    () =>
      statuses
        .filter((s) => KPI_STATUS_NAMES.includes((s.name ?? '').trim().toLowerCase()))
        .sort(
          (a, b) =>
            KPI_STATUS_NAMES.indexOf((a.name ?? '').trim().toLowerCase()) -
            KPI_STATUS_NAMES.indexOf((b.name ?? '').trim().toLowerCase()),
        ),
    [statuses],
  );

  // ── Stream registrations ────────────────────────────────────────────────
  // Streamed in 50-row batches so rows paint as they arrive. Drawer filters go server-side through
  // the stream filter; `search` stays client-side because re-streaming on every keystroke would be
  // wasteful and the full filtered set is already in memory.
  const {
    items: registrations,
    isLoading: loading,
    progress,
    loadedCount,
    load: fetchAll,
  } = useRegistrationStreaming(appliedFilter, 50);

  // ── Campus scope ────────────────────────────────────────────────────────
  // Only registrations whose Location belongs to this campus ("Glen Austin" etc.). Search, sort,
  // export, pagination and the KPI counts all work from this set.
  const campusRows = useMemo(
    () => registrations.filter((r) => matchesCampus(campus, r.locationName)),
    [registrations, campus],
  );

  // Kept in a ref so the sidebar-count refresh doesn't participate in the stream effect's deps.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    fetchAll().then(() => refetchRef.current());
  }, [fetchAll]);

  useEffect(() => { setActivePage(1); }, [searchTerm, appliedFilter]);

  // ── Fetch lookups ───────────────────────────────────────────────────────
  const fetchLookups = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([
        courseService.getAll(),
        facilitatorService.list(),
      ]);
      setCourses((c as any)?.data ?? c ?? []);
      setFacilitators((f as any)?.data ?? f ?? []);
    } catch (e) {
      console.error('Error fetching registration lookups:', e);
    }
    setStatusesLoading(true);
    try {
      const s = await statusService.getAll();
      setStatuses((s as any)?.data ?? s ?? []);
    } catch (e) {
      console.error('Error fetching statuses:', e);
    } finally {
      setStatusesLoading(false);
    }
  }, [courseService, facilitatorService, statusService]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  // Course dates cascade off the selected course, mirroring the registration wizard.
  useEffect(() => {
    if (!filterCourseID) {
      setCourseDates([]);
      return;
    }
    courseService
      .getDates(Number(filterCourseID))
      .then((d) => setCourseDates((d as any)?.data ?? d ?? []))
      .catch((e) => console.error('Error fetching course dates:', e));
  }, [filterCourseID, courseService]);

  // ── Global per-status counts for the KPI cards ──────────────────────────
  // Deliberately independent of the applied filter, so selecting a status does not collapse
  // every other card to zero.
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [allCount, setAllCount] = useState(0);
  const [countsLoading, setCountsLoading] = useState(true);

  // Counted from this campus's rows. The stream is filtered server-side, so counting while a filter is
  // active would collapse the other cards; the counts are held until the filters are cleared.
  useEffect(() => {
    if (!showStatusKPIs || isFilterActive) return;
    const counts: Record<string, number> = {};
    campusRows.forEach((r) => {
      const key = String((r as any).statusID);
      counts[key] = (counts[key] ?? 0) + 1;
    });
    setAllCount(campusRows.length);
    setStatusCounts(counts);
    setCountsLoading(loading);
  }, [campusRows, loading, isFilterActive, showStatusKPIs]);

  // ── Filter handlers ─────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setAppliedFilter({
      statusID:          fixedStatusID !== undefined ? String(fixedStatusID) : filterStatus,
      courseID:          filterCourseID,
      dateID:            filterDateID,
      facilitatorUserId: filterFacilitatorUserId,
    });
    setActivePage(1);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setFilterStatus(fixedStatusID !== undefined ? String(fixedStatusID) : '');
    setFilterCourseID('');
    setFilterDateID('');
    setFilterFacilitatorUserId('');
    setAppliedFilter(fixedStatusID !== undefined ? { statusID: String(fixedStatusID) } : {});
    setActivePage(1);
  };

  // ── Client-side search over the streamed set ──────────
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return campusRows;
    return campusRows.filter((r) =>
      [r.reference, r.description, r.courseName, r.locationName, r.leadFacilitatorName, r.statusName]
        .some((field) => (field ?? '').toLowerCase().includes(term))
      || String(r.registrationID ?? '').includes(term),
    );
  }, [campusRows, searchTerm]);

  const facilitatorLabel = (f: any) =>
    [f.firstName, f.surname].filter(Boolean).join(' ').trim()
    || f.fullName?.trim()
    || f.displayName?.trim()
    || f.userId;

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns: TableColumn<RegistrationRow>[] = [
    { key: 'registrationID', header: 'ID', accessor: (r) => r.registrationID, sortable: true, width: 90 },
    { key: 'description', header: 'Description', accessor: (r) => r.description ?? '—', sortable: true },
    { key: 'Date', header: 'Date', accessor: (r) => r.periodDisplay ?? '—', sortable: true },
    {
      // Pre-formatted "d MMM yyyy" from the API, so it is printed as-is rather than run through
      // formatSDKDate, which expects yyyyMMddHHmmss.
      key: 'registeredDate', header: 'Registered', accessor: (r) => r.registeredDate ?? '—', sortable: true,
    },
    {
      key: 'courseID',
      header: 'Course',
      accessor: (r) => r.courseName ?? `Course ${r.courseID}`,
      sortable: true,
      render: (value, r) =>
        r.courseName ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.courseColor)}>
            {value}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">{value as string}</Text>
        ),
    },
    ...(showStatusKPIs
      ? [{
          key: 'statusID',
          header: 'Status',
          accessor: (r: RegistrationRow) => r.statusName ?? String(r.statusID ?? '—'),
          sortable: true,
          align: 'center' as const,
          render: (value: unknown, r: RegistrationRow) => (
            <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.statusColor)}>
              {value as string}
            </Badge>
          ),
        }]
      : []),
    {
      key: 'locationName', header: 'Location', accessor: (r) => r.locationName ?? '—', sortable: true,
      render: (value, r) => (r.locationName
        ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.locationColor)}>
            {value}
          </Badge>
        )
        : '—'),
    },
    {
      key: 'Facilitators', header: 'Facilitator',
      accessor: (r) => r.leadFacilitatorName ?? '—', sortable: true,
      render: (value) => <Text size="sm">{(value as string) || '—'}</Text>,
    },
    { key: 'Sessions', header: 'Sessions', accessor: (r) => r.sessionCount ?? '—', sortable: true, align: 'center' },
    {
      key: 'actions',
      header: '',
      accessor: (r) => r,
      sortable: false,
      align: 'center',
      width: 110,
      render: (_, row) => (
        <Group gap={6} justify="center" wrap="nowrap">
          <Tooltip label="View">
            <ActionIcon variant="light" color="blue" size="sm"
              onClick={(e) => { e.stopPropagation(); openRegistration(row.registrationID); }}>
              <IconEye size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ];

  /**
   * ⚠️ Sorting lives here, not in `CCMSDataTable`. The table only draws the arrow and calls `onSort`;
   * this page previously threw that away, so the arrows moved and the rows never did.
   * `sortRows` keys off each column's own `accessor`, so it cannot disagree with the cell.
   *
   * Declared **after** `columns` on purpose: `useMemo` runs during render, so referencing `columns`
   * from above it would hit the temporal dead zone.
   */
  const sorted = useMemo(
    () => sortRows(filtered, columns, sortConfig),
    [filtered, columns, sortConfig],
  );

  const paginated = useMemo(
    () => sorted.slice((activePage - 1) * pageSize, activePage * pageSize),
    [sorted, activePage, pageSize],
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      {/* Header: title/subtitle left, Refresh far right, matching /Counselling/Cases */}
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb="lg">
        <div>
          <Title order={2}>{title ?? 'Registrations'}</Title>
          <Text c="dimmed">{subtitle ?? 'Manage course registrations.'}</Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={() => fetchAll()} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Status KPI cards (All Registrations page only) */}
      {showStatusKPIs && (
        <SimpleGrid cols={{ base: 2, sm: 3, lg: KPI_CARD_COUNT }} spacing="md" mb="md">
          {(countsLoading || statusesLoading) ? (
            Array.from({ length: KPI_CARD_COUNT }).map((_, i) => <StatusKPISkeleton key={i} />)
          ) : (
            <>
              <StatusKPICard
                icon={IconListDetails}
                label="All"
                count={allCount}
                color="#4dabf7"
                active={filterStatus === ''}
                onClick={() => {
                  setFilterStatus('');
                  setAppliedFilter((f) => { const { statusID, ...rest } = f; return rest; });
                  setActivePage(1);
                }}
              />
              {visibleStatuses.map((s) => (
                <StatusKPICard
                  key={s.statusID}
                  icon={getStatusIcon(s.icon)}
                  label={s.name}
                  count={statusCounts[String(s.statusID)] ?? 0}
                  color={s.color || '#868e96'}
                  active={filterStatus === String(s.statusID)}
                  onClick={() => {
                    const next = filterStatus === String(s.statusID) ? '' : String(s.statusID);
                    setFilterStatus(next);
                    setAppliedFilter((f) => ({ ...f, statusID: next || undefined }));
                    setActivePage(1);
                  }}
                />
              ))}
            </>
          )}
        </SimpleGrid>
      )}

      {/* Search & filter bar */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by reference, description, course, or facilitator…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '320px' } }}
              size="sm"
            />
            <Button variant="outline" leftSection={<IconAdjustments size={16} />} onClick={() => setFiltersOpen(true)} disabled={loading}>
              Filters
              {isFilterActive && <Badge ml="xs" size="xs" color="blue" circle>!</Badge>}
            </Button>
            {isFilterActive && (
              <Button variant="subtle" color="red" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <Flex gap="sm" align="center" wrap="wrap">
            <ExportCsvButton
              data={sorted}
              columns={columns}
              filename="registrations"
            />
          </Flex>
        </Flex>
      </Card>

      {/* Filter drawer */}
      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filter Registrations"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          {/* Status is pinned on the status-locked pages, so it is only offered here */}
          {showStatusKPIs && (
            <Select
              label="Status"
              placeholder="All Statuses"
              data={[
                { value: '', label: 'All Statuses' },
                ...statuses.map((s) => ({ value: String(s.statusID), label: s.name })),
              ]}
              value={filterStatus}
              onChange={(v) => setFilterStatus(v ?? '')}
              clearable
            />
          )}

          <Select
            label="Course"
            placeholder="All Courses"
            data={courses.map((c) => ({
              value: String(c.courseID),
              label: c.name ?? c.courseName ?? `Course ${c.courseID}`,
            }))}
            value={filterCourseID}
            onChange={(v) => {
              setFilterCourseID(v ?? '');
              // A date belongs to a course, so changing the course invalidates the chosen date.
              setFilterDateID('');
            }}
            searchable
            clearable
          />

          <Select
            label="Course Date"
            placeholder={filterCourseID ? 'All Dates' : 'Pick a course first'}
            description={filterCourseID ? undefined : 'Intake dates belong to a course.'}
            disabled={!filterCourseID}
            data={courseDates.map((d) => ({
              value: String(d.dateID),
              label: d.periodDisplay
                ?? [d.startDateTime, d.endDateTime].filter(Boolean).join(' to ')
                ?? `Date ${d.dateID}`,
            }))}
            value={filterDateID}
            onChange={(v) => setFilterDateID(v ?? '')}
            searchable
            clearable
          />

          <Select
            label="Facilitator"
            placeholder="All Facilitators"
            data={facilitators.map((f) => ({ value: String(f.userId), label: facilitatorLabel(f) }))}
            value={filterFacilitatorUserId}
            onChange={(v) => setFilterFacilitatorUserId(v ?? '')}
            searchable
            clearable
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => { handleClearFilters(); setFiltersOpen(false); }}>
              Clear
            </Button>
            <Button onClick={handleApplyFilters}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Table */}
      <Card withBorder radius="md" padding="lg">
        <StreamProgress
          loading={loading}
          progress={progress}
          loadedCount={loadedCount}
          label="registrations"
        />
        <CCMSDataTable
          data={paginated}
          columns={columns}
          loading={loading && registrations.length === 0}
          sortable
          onSort={(config) => { setSortConfig(config); setActivePage(1); }}
          onRowClick={(r) => openRegistration(r.registrationID)}
          striped
          highlightOnHover
        />
        <ListPagination
          page={activePage}
          pageSize={pageSize}
          totalItems={filtered.length}
          onPageChange={setActivePage}
          onPageSizeChange={(size) => { setPageSize(size); setActivePage(1); }}
          showPageSize
        />
      </Card>
    </Container>
  );
}